import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import axios from 'axios';
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { ConfirmEvent, useModal } from 'yoso';
import { ChallengeScoreUpBottomSheet } from '~/components/Challenge/Score/BottomSheet/ChallengeScoreUpBottomSheet';
import { useScoreUpProcedureState } from '~/components/Challenge/Score/Effect/ChallengeScoreUpEffect.hooks';
import { ChallengeScoreUpValues } from '~/components/Challenge/Score/Form/ChallengeScoreUpForm.hooks';
import { ScoreUpFulfilledPopup } from '~/components/Challenge/Score/Fulfilled/ScoreUpFulfilledPopup';
import { BaseError } from '~/components/Error/BaseError';
import { HttpError } from '~/components/Error/HttpError';
import { Translation } from '~/components/I18n';
import { useMember } from '~/components/Member/Member.hooks';
import { useBottomSheet } from '~/components/Modal/BottomSheet';
import { UseDosiminMutationOptions, UseDosiminQueryOptions } from '~/components/Query/Query.hooks';
import { AutoSizeText } from '~/components/Text/AutoSize/AutoSizeText';
import { format } from '~/components/Util/number';
import styles from './BottomSheet/ChallengeScoreUpBottomSheet.module.scss';

export type ChallengeScore = {
  week: number;
  score: number;
  purchaseRewardScore: number;
  scoreUpScore: number;
  scoreUpCount: number;
  scoreUpMaxCount: number;
};

export type ChallengeScoreUpCriteria = {
  baseCount: number;
  baseScore: number;
  maxCountPerJoin: number;
  joinForAssetAmount: number;
  assetType: 'DON' | string;
};

export const challengeScoreUrl = '/api/citizen/v1/challenge/score';
export const getChallengeScore = async () => {
  const { data } = await axios.get<ChallengeScore>(challengeScoreUrl);
  return data;
};
export const useChallengeScore = (options?: UseDosiminQueryOptions<ChallengeScore>) =>
  useQuery<ChallengeScore>({
    queryKey: ['challenge', 'score'],
    queryFn: getChallengeScore,
    initialData: {
      score: 0,
      purchaseRewardScore: 0,
      scoreUpScore: 0,
      week: 0,
      scoreUpCount: 0,
      scoreUpMaxCount: 0,
    },
    initialDataUpdatedAt: 0,
    ...options,
  });
export type ChallengeScoreUpHistory = {
  count: number;
  scoreUpScore: number;
  joinForAssetAmount: number;
  assetType: 'DON' | string;
  participatedAt: Date;
};

export type ChallengeScoreUpHistoryParams = {
  week?: number;
  pageNo?: number;
  pageSize?: number;
};

export type ChallengeScoreUpHistoriesResponse = {
  pageNo: number;
  last: boolean;
  week: number;
  totalScore: number;
  totalScoreUpScore: number;
  historyList: ChallengeScoreUpHistory[];
};

export const challengeScoreUpHistoriesUrl = '/api/citizen/v1/challenge/score-up/history';

export const getChallengeScoreUpHistories = async (params: ChallengeScoreUpHistoryParams) => {
  const { data } = await axios.get<ChallengeScoreUpHistoriesResponse>(
    challengeScoreUpHistoriesUrl,
    {
      params,
    }
  );
  return {
    ...data,
    historyList: data.historyList.map(({ participatedAt, ...rest }) => ({
      ...rest,
      participatedAt: new Date(participatedAt),
    })),
  };
};

export type ScoreUpCriteriaParams = {
  week?: number;
};

export const useChallengeScoreUpHistories = (
  params?: ChallengeScoreUpHistoryParams,
  options?: UseInfiniteQueryOptions<ChallengeScoreUpHistoriesResponse>
) =>
  useInfiniteQuery<ChallengeScoreUpHistoriesResponse>({
    queryKey: ['challenge', 'event', params?.week, 'score', 'history'],
    queryFn: ({ pageParam }) => getChallengeScoreUpHistories({ ...params, ...pageParam }),
    getNextPageParam: (page) => (page?.last ? undefined : page?.pageNo + 1),
    initialData: {
      pages: [],
      pageParams: [],
    },
    ...options,
  });

export const useHomeProcedureScoreUpSuccessHandler = () => {
  const queryClient = useQueryClient();
  const { addProcedureCount } = useScoreUpProcedureState();

  return useCallback(
    ({ scoreUp: updatedScore }: ScoreUp) => {
      queryClient.invalidateQueries(['challenge', 'boost']);
      queryClient.invalidateQueries(['challenge', 'leaderboard']);

      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

      toast(
        <>
          <AutoSizeText className={styles.score}>
            <Translation name="citizen_unit_score" pre="nowrap" options={[format(updatedScore)]} />
          </AutoSizeText>
          UP
        </>,
        {
          className: styles.toast,
          bodyClassName: styles.toastBody,
          // Enforce 'center' position
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          position: 'center' as any,
          autoClose: 1000,
          pauseOnHover: false,
          pauseOnFocusLoss: false,
          onClose: () => {
            addProcedureCount();

            window.setTimeout(() => {
              queryClient.setQueriesData<ChallengeScore>(['challenge', 'score'], (previous) => ({
                ...(previous as ChallengeScore),
                score: (previous?.score || 0) + updatedScore,
                scoreUpScore: (previous?.scoreUpScore || 0) + updatedScore,
              }));
            }, 1000);
          },
        }
      );
    },
    [addProcedureCount, queryClient]
  );
};

export const useDefaultProcedureScoreUpSuccessHandler = () => {
  const queryClient = useQueryClient();

  return useCallback(
    ({ scoreUp: updatedScore }: ScoreUp) => {
      queryClient.invalidateQueries(['challenge', 'score']);
      queryClient.invalidateQueries(['challenge', 'boost']);
      queryClient.invalidateQueries(['challenge', 'leaderboard']);

      toast(
        <>
          <AutoSizeText className={styles.score}>
            <Translation name="citizen_unit_score" pre="nowrap" options={[format(updatedScore)]} />
          </AutoSizeText>
          UP
        </>,
        {
          className: styles.toast,
          bodyClassName: styles.toastBody,
          // Enforce 'center' position
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          position: 'center' as any,
          autoClose: 2000,
          pauseOnHover: false,
          pauseOnFocusLoss: false,
        }
      );
    },
    [queryClient]
  );
};

export const useProceedScoreUpErrorHandler = () => {
  const modal = useModal();

  return useCallback(
    (error: BaseError) => {
      switch (error.message) {
        case 'SCORE_UP_MAXED_OUT':
          modal.open(<ScoreUpFulfilledPopup />);
          break;
        default:
          break;
      }
    },
    [modal]
  );
};

export const useProceedScoreUp = (options?: UseDosiminMutationOptions<ScoreUp, BaseError>) => {
  const bottomSheet = useBottomSheet();
  const onSuccess = useDefaultProcedureScoreUpSuccessHandler();
  const onError = useProceedScoreUpErrorHandler();
  const {
    data: { isLoggedIn },
  } = useMember();
  const { mutateAsync: scoreUp } = useScoreUp();
  const {
    data: { scoreUpCount = 0, scoreUpMaxCount = 0 },
  } = useChallengeScore({ enabled: isLoggedIn });
  const mutation = useMutation({
    mutationFn: async () => {
      if (scoreUpCount >= scoreUpMaxCount) {
        throw new BaseError('SCORE_UP_MAXED_OUT');
      }

      const { count } = (await bottomSheet.open(<ChallengeScoreUpBottomSheet />, {
        throwOnDismiss: true,
      })) as ConfirmEvent<ChallengeScoreUpValues>;

      return await scoreUp({ count });
    },
    onSuccess,
    onError,
    ...options,
  });

  return mutation;
};

export const scoreUpCriteriaUrl = '/api/citizen/v1/challenge/score-up/criteria';
export const getScoreUpCriteria = async (params?: ScoreUpCriteriaParams) => {
  const { data } = await axios.get<ChallengeScoreUpCriteria>(scoreUpCriteriaUrl, { params });
  return data;
};

export const useScoreUpCriteria = (
  options?: UseDosiminQueryOptions<ChallengeScoreUpCriteria, BaseError>
) =>
  useQuery<ChallengeScoreUpCriteria, BaseError>({
    queryKey: ['challenge', 'score', 'criteria'],
    queryFn: async () => await getScoreUpCriteria(),
    initialData: {
      baseCount: 1,
      baseScore: 1000,
      maxCountPerJoin: 100000,
      joinForAssetAmount: 15000,
      assetType: 'DON',
    },
    ...options,
  });

export type ScoreUpVariables = {
  count: number;
};

export type ScoreUp = {
  totalCount: number;
  scoreUp: number;
};

export const scoreUpUrl = '/api/citizen/v1/challenge/score-up';
export const scoreUp = async (variables: ScoreUpVariables) => {
  const { data } = await axios.post<ScoreUp>(scoreUpUrl, variables);
  return data;
};

export const useScoreUp = (
  options?: UseDosiminMutationOptions<ScoreUp, HttpError, ScoreUpVariables>
) =>
  useMutation<ScoreUp, HttpError, ScoreUpVariables>({
    mutationFn: scoreUp,
    ...options,
  });
